// Pure helpers for DataTable. Nothing here knows what a row represents.

/**
 * Reads `path` from `obj`. An exact own key wins (so keys containing dots
 * still work); otherwise the path is walked, e.g. "owner.name".
 */
export const getValueByPath = (obj, path) => {
  if (obj == null) return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, path)) return obj[path];
  return String(path)
    .split(".")
    .reduce((acc, part) => (acc == null ? undefined : acc[part]), obj);
};

export const getCellValue = (row, column) =>
  column.accessor ? column.accessor(row) : getValueByPath(row, column.key);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ][\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;

/** Normalises a raw value into something comparable, or null when it can't be sorted. */
const toSortable = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === "string") {
    if (ISO_DATE.test(value)) {
      const t = Date.parse(value);
      if (!Number.isNaN(t)) return t;
    }
    return value;
  }
  if (Array.isArray(value)) return value.join(", ");
  return null;
};

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

const compare = (a, b) => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return collator.compare(String(a), String(b));
};

/**
 * Stable sort by one column. Empty values always go last, whichever direction.
 * `column.sortValue(row)` overrides the value used for comparison.
 */
export const sortRows = (rows, column, direction) => {
  const factor = direction === "desc" ? -1 : 1;
  return rows
    .map((row, index) => ({
      row,
      index,
      value: toSortable(column.sortValue ? column.sortValue(row) : getCellValue(row, column)),
    }))
    .sort((a, b) => {
      if (a.value === null && b.value === null) return a.index - b.index;
      if (a.value === null) return 1;
      if (b.value === null) return -1;
      return compare(a.value, b.value) * factor || a.index - b.index;
    })
    .map((entry) => entry.row);
};

const toSearchText = (value) => {
  if (value == null) return "";
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? "" : value.toISOString();
  if (Array.isArray(value)) return value.map(toSearchText).join(" ");
  if (typeof value === "object") return ""; // opaque objects need column.searchValue
  return String(value);
};

/**
 * Columns used for client-side search: those marked `searchable: true`, or,
 * when none are marked, every column except ones marked `searchable: false`.
 */
export const getSearchColumns = (columns) => {
  const flagged = columns.filter((c) => c.searchable === true);
  return flagged.length ? flagged : columns.filter((c) => c.searchable !== false);
};

export const filterRows = (rows, columns, query) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;
  const searchColumns = getSearchColumns(columns);
  return rows.filter((row) =>
    searchColumns.some((column) => {
      const text = column.searchValue
        ? column.searchValue(row)
        : toSearchText(getCellValue(row, column));
      return String(text).toLowerCase().includes(needle);
    }),
  );
};

/** Page list with ellipses, e.g. [1, "ellipsis", 4, 5, 6, "ellipsis", 12]. */
export const getPageItems = (page, pageCount, siblings = 1) => {
  const total = siblings * 2 + 5; // first, last, current, siblings, 2 ellipses
  if (pageCount <= total) return Array.from({ length: pageCount }, (_, i) => i + 1);

  const start = Math.max(2, page - siblings);
  const end = Math.min(pageCount - 1, page + siblings);
  const showLeft = start > 3;
  const showRight = end < pageCount - 2;

  if (!showLeft) {
    const count = siblings * 2 + 3;
    return [...Array.from({ length: count }, (_, i) => i + 1), "ellipsis", pageCount];
  }
  if (!showRight) {
    const count = siblings * 2 + 3;
    return [1, "ellipsis", ...Array.from({ length: count }, (_, i) => pageCount - count + 1 + i)];
  }
  return [1, "ellipsis", ...Array.from({ length: end - start + 1 }, (_, i) => start + i), "ellipsis", pageCount];
};

export const cx = (...classes) => classes.filter(Boolean).join(" ");

export const alignClass = { left: "text-left", center: "text-center", right: "text-right" };
export const justifyClass = { left: "justify-start", center: "justify-center", right: "justify-end" };
